import React, { useEffect } from 'react';
import { XMarkIcon, InformationCircleIcon, ExclamationTriangleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

export interface ToastItem {
  id: string;
  message: string;
  type?: 'info' | 'success' | 'error';
  duration?: number;
}

interface ToastProps {
  item: ToastItem;
  onClose: (id: string) => void;
  index: number;
}

const Toast: React.FC<ToastProps> = ({ item, onClose, index }) => {
  const { id, message, type = 'info', duration = 7000 } = item;

  useEffect(() => {
    const timer = setTimeout(() => {
      onClose(id);
    }, duration);
    return () => clearTimeout(timer);
  }, [id, onClose, duration]);

  const iconColor = type === 'success'
    ? 'text-green-500'
    : type === 'error'
      ? 'text-red-500'
      : 'text-orange-500';

  const bgColor = type === 'success'
    ? 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800'
    : type === 'error'
      ? 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800'
      : 'bg-white/95 dark:bg-claude-darkSurface/95 border-claude-border/60 dark:border-claude-darkBorder/60';

  const iconBg = type === 'success'
    ? 'bg-green-100 dark:bg-green-800/40'
    : type === 'error'
      ? 'bg-red-100 dark:bg-red-800/40'
      : 'bg-orange-100 dark:bg-orange-800/40';

  const Icon = type === 'success'
    ? CheckCircleIcon
    : type === 'error'
      ? ExclamationTriangleIcon
      : InformationCircleIcon;

  return (
    <div
      className="animate-slide-in-right"
      style={{ marginTop: index === 0 ? '1rem' : '0.5rem' }}
    >
      <div className={`w-full max-w-sm rounded-xl border ${bgColor} text-claude-text dark:text-claude-darkText px-4 py-3 shadow-lg backdrop-blur-md`}>
        <div className="flex items-center gap-3">
          <div className={`shrink-0 rounded-full ${iconBg} p-2`}>
            <Icon className={`h-4 w-4 ${iconColor}`} />
          </div>
          <div className="flex-1 text-sm leading-snug line-clamp-3">
            {message}
          </div>
          <button
            onClick={() => onClose(id)}
            className="shrink-0 text-claude-textSecondary dark:text-claude-darkTextSecondary hover:text-claude-text dark:hover:text-claude-darkText rounded-full p-1 hover:bg-claude-surfaceHover dark:hover:bg-claude-darkSurfaceHover transition-colors"
            aria-label="Close"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

interface ToastContainerProps {
  toasts: ToastItem[];
  onDismiss: (id: string) => void;
}

const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, onDismiss }) => {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-0 right-4 z-50 flex flex-col items-end">
      {toasts.map((toast, index) => (
        <Toast
          key={toast.id}
          item={toast}
          onClose={onDismiss}
          index={index}
        />
      ))}
    </div>
  );
};

export { ToastContainer };
export default Toast;